import Sidebar from '../sidebar/sidebar'
import { Nullable } from '../../types/common'
import { getFocusableTargets } from '../../utils/utils'
import DrawerHeaderPage from './drawer-menu-page'
import HeaderComponent from './header'

class DrawerMenu extends Sidebar {
  openedPage: DrawerHeaderPage
  header: Nullable<HeaderComponent>
  pages: DrawerHeaderPage[]
  pageMap: { [key: string]: DrawerHeaderPage }
  homePage: DrawerHeaderPage
  toggler: Nullable<HTMLElement>

  constructor() {
    super()

    this.header = document.querySelector<HeaderComponent>('header-component')
    this.pages = [
      ...this.element.querySelectorAll('drawer-menu-page'),
    ] as DrawerHeaderPage[]
    this.pageMap = this.generatePageMap()
    this.homePage = this.pageMap['Menu']
    this.toggler = document.getElementById('DrawerMenuToggler')
  }

  connectedCallback(): void {
    super.connectedCallback()

    this.addEventListener('keydown', this.handlePageKeydown)

    if (this.isEditorMode) {
      this.editor.subscribe('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.subscribe('BLOCK_DESELECT', this.handleBlockDeselect)
      this.editor.subscribe('SECTION_SELECT', this.handleSectionSelect)
      this.editor.subscribe('SECTION_DESELECT', this.handleSectionDeselect)
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()

    this.removeEventListener('keydown', this.handlePageKeydown)

    if (this.isEditorMode) {
      this.editor.destroy()
    }
  }

  handleSectionLoad = (): void => {
    //
  }

  handleSectionSelect = ({ detail: { sectionId } }): void => {
    if (this.isVisible && this.dataset.sectionId === sectionId) {
      this.mount()

      this.reset()
    }
  }

  handleSectionDeselect = ({ detail: { sectionId } }): void => {
    if (this.isVisible && this.dataset.sectionId === sectionId) {
      this.reset()

      this.hide(true)
    }
  }

  handleBlockSelect = ({ detail: { blockId, sectionId } }): void => {
    if (this.dataset.sectionId === sectionId) {
      const target = this.pages.find(page => {
        return page.getAttribute('block-id') === blockId
      })

      if (!target) {
        return
      }

      this.open()

      this.setPage(target)
    }
  }

  handleBlockDeselect = ({ detail: { blockId, sectionId } }): void => {
    if (this.dataset.sectionId === sectionId) {
      const target = this.pages.find(page => {
        return page.getAttribute('block-id') === blockId
      })

      if (!target) {
        return
      }

      this.hide(true)
    }
  }

  handlePageKeydown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement
    const page = target.closest('drawer-menu-page')

    if (!page) {
      return
    }

    const targets = page.querySelectorAll(getFocusableTargets())

    const isLastTarget = target === targets[targets.length - 1]
    const isFirstTarget = target === targets[0]
    const isShift = event.shiftKey

    if (event.keyCode !== 9) {
      return
    }

    if ((isShift && isFirstTarget) || (!isShift && isLastTarget)) {
      event.preventDefault()

      this.focusOnToggler()
    }
  }

  focusOnToggler() {
    if (!this.toggler) {
      return
    }

    this.toggler.focus()

    this.hide()
  }

  generatePageMap() {
    const pageMap = this.pages.reduce((map, page) => {
      const pageId = page.dataset.pageId

      return pageId ? { ...map, [pageId]: page } : map
    }, {})

    return pageMap
  }

  setPage(page: DrawerHeaderPage): void {
    const pageId = page.dataset.pageId
    const prevOpenedPageId = this.openedPage?.dataset.prevPageId
    const isReturn = pageId === prevOpenedPageId
    const isNextPageEmpty = page.hasAttribute('data-empty-menu')

    if (this.openedPage) {
      if (isReturn || isNextPageEmpty) {
        this.setPageVisible(this.openedPage, false)
      }
    }

    const firstFocusTarget = page.querySelector(getFocusableTargets())

    if (firstFocusTarget) {
      firstFocusTarget.focus()
    }

    this.setPageVisible(page, true)

    this.openedPage = page
  }

  reset(): void {
    this.pages.forEach(page => {
      this.setPageVisible(page, false)
    })

    this.setPageVisible(this.homePage, true)

    this.openedPage = this.homePage
  }

  setPageVisible(page: DrawerHeaderPage, isVisible: boolean): void {
    page.setAttribute('aria-hidden', isVisible ? 'false' : 'true')
  }

  open(): Promise<void> {
    if (this.header) {
      this.header.updatePosition()
    }

    return super.open()
  }

  hide(isInstant?: boolean): Promise<void> {
    this.reset()

    return super.hide(isInstant)
  }

  getPage(pageId: string): Nullable<DrawerHeaderPage> {
    return this.pageMap[pageId]
  }

  get isVisible(): boolean {
    const isLowResolution = matchMedia('(max-width: 1016px)').matches
    const isSection = this.hasAttribute('data-drawer-menu-is-section')

    return isSection || isLowResolution
  }
}

export default DrawerMenu
